// src/components/Sidebar.js
import React, { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import logo from '../vendor/logo.png';

// Icons
import DashboardIcon from './icons/Dashboard.svg';
import AttacksIcon from './icons/Firewall-Rules.svg';
import ApplicationProtectIcon from './icons/Application-Protect.svg';
import FirewallRulesIcon from './icons/Firewall-Rules.svg';
import AnalyticsIcon from './icons/Analytics.svg';
import Layer7Icon from './icons/Layer7.svg';
import VulnerabilityManagerIcon from './icons/Vulnerability-Manager.svg';
import TrafficAnalyzerIcon from './icons/Traffic-Analyzer.svg';

function Sidebar() {
  const linkClass =
    'flex items-center h-18 pl-4 rounded hover:bg-[#262F3D] text-white text-lg';
  const activeLinkClass = 'bg-[#262F3D]';

  // States to manage tooltip visibility
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const tooltipRef = useRef(null);
  const containerRef = useRef(null);

  // Handle clicks outside the tooltip to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsClicked(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Determine if tooltip should be visible
  const isTooltipVisible = isHovered || isClicked;

  // Handlers for mouse events
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleClick = () => {
    setIsClicked((prev) => !prev);
  };

  return (
    <div className="z-50 w-1/6 h-full bg-[#172031] rounded-r-2xl p-4 fixed flex flex-col justify-between">
      {/* Top Section */}
      <div>
        <div className="mb-8">
          <img src={logo} alt="Logo" className="h-12" />
        </div>

        <ul className="font-bold">
          {/* 1) Dashboard */}
          <li className="mb-2">
            <NavLink
              to="/"
              className={({ isActive }) =>
                isActive ? `${linkClass} ${activeLinkClass}` : linkClass
              }
              style={{ height: '75px' }}
            >
              <img
                src={DashboardIcon}
                alt="Dashboard"
                className="inline-block mr-3"
              />
              Dashboard
            </NavLink>
          </li>

          {/* 2) Attacks */}
          <li className="mb-2">
            <NavLink
              to="/attacks"
              className={({ isActive }) =>
                isActive ? `${linkClass} ${activeLinkClass}` : linkClass
              }
              style={{ height: '75px' }}
            >
              <img
                src={AttacksIcon}
                alt="Attacks"
                className="inline-block mr-3"
              />
              Attacks
            </NavLink>
          </li>

          {/* Blurred Links */}
          <div className="relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-60 flex items-center justify-center z-10">
              <span className="text-white text-3xl font-bold">Coming Soon</span>
            </div>
            <div className="blur-sm">
              <li className="mb-2">
                <NavLink
                  to="/application-protect"
                  className={({ isActive }) =>
                    isActive ? `${linkClass} ${activeLinkClass}` : linkClass
                  }
                  style={{ height: '75px' }}
                >
                  <img
                    src={ApplicationProtectIcon}
                    alt="Application Protect"
                    className="inline-block mr-3"
                  />
                  Application Protect
                </NavLink>
              </li>
              <li className="mb-2">
                <NavLink
                  to="/firewall-rules"
                  className={({ isActive }) =>
                    isActive ? `${linkClass} ${activeLinkClass}` : linkClass
                  }
                  style={{ height: '75px' }}
                >
                  <img
                    src={FirewallRulesIcon}
                    alt="Firewall Rules"
                    className="inline-block mr-3"
                  />
                  Firewall Rules
                </NavLink>
              </li>
              <li className="mb-2">
                <NavLink
                  to="/analytics"
                  className={({ isActive }) =>
                    isActive ? `${linkClass} ${activeLinkClass}` : linkClass
                  }
                  style={{ height: '75px' }}
                >
                  <img
                    src={AnalyticsIcon}
                    alt="Analytics"
                    className="inline-block mr-3"
                  />
                  Analytics
                </NavLink>
              </li>
              <li className="mb-2">
                <NavLink
                  to="/layer7-attacks"
                  className={({ isActive }) =>
                    isActive ? `${linkClass} ${activeLinkClass}` : linkClass
                  }
                  style={{ height: '75px' }}
                >
                  <img
                    src={Layer7Icon}
                    alt="Layer 7 Attacks"
                    className="inline-block mr-3"
                  />
                  Layer 7 Attacks
                </NavLink>
              </li>
              <li className="mb-2">
                <NavLink
                  to="/vulnerability-manager"
                  className={({ isActive }) =>
                    isActive ? `${linkClass} ${activeLinkClass}` : linkClass
                  }
                  style={{ height: '75px' }}
                >
                  <img
                    src={VulnerabilityManagerIcon}
                    alt="Vulnerability Manager"
                    className="inline-block mr-3"
                  />
                  Vulnerability Manager
                </NavLink>
              </li>
              <li className="mb-2">
                <NavLink
                  to="/traffic-analyzer"
                  className={({ isActive }) =>
                    isActive ? `${linkClass} ${activeLinkClass}` : linkClass
                  }
                  style={{ height: '75px' }}
                >
                  <img
                    src={TrafficAnalyzerIcon}
                    alt="Traffic Analyzer"
                    className="inline-block mr-3"
                  />
                  Traffic Analyzer
                </NavLink>
              </li>
            </div>
          </div>
        </ul>
      </div>

      {/* Bottom Section */}
      <div className="flex items-center space-x-2">
        {/* Version Label and Tooltip */}
        <div
          className="relative w-1/4"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          ref={containerRef}
        >
          <div
            onClick={handleClick}
            className="text-white text-xs bg-[#262F3D] rounded-full px-2 py-1 text-center cursor-pointer select-none"
            aria-haspopup="true"
            aria-expanded={isTooltipVisible}
          >
            v0.1.1
          </div>
          {isTooltipVisible && (
            <div
              ref={tooltipRef}
              className="absolute bottom-full left-0 mb-2 bg-gray-700 text-white text-xs rounded px-4 py-2 w-64 text-center z-20"
            >
              This is version v0.1.1 of the Hybrid Protect Panel. If you would
              like to learn more, visit our{' '}
              <a
                href="https://hybridhosting.co/blog/info/hybrid-protect-release-notes-v0.1.0"
                className="text-blue-400 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                release notes
              </a>
              .
            </div>
          )}
        </div>

        {/* Go to Main Site Button */}
        <a
          href="https://hybridhosting.co"
          target="_blank"
          rel="noopener noreferrer"
          className="w-3/4 bg-[#0EA5E9] text-white text-xs rounded-full px-4 py-1 flex items-center justify-center hover:bg-[#0078B8] transition-colors duration-300"
        >
          Go to Main Site
        </a>
      </div>
    </div>
  );
}

export default Sidebar;